import { useLangFile } from '../../context/LanguageContext';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import React from 'react';

const styles = (theme) => ({
  wrapper: {
    position: 'relative',
    width: '100%',
  },
  scale: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    width: '100%',
    position: 'relative',
  },
  scaleStep: {
    lineHeight: '20px',
  },
  minMaxTick: {
    position: 'absolute',
    userSelect: 'none',
    pointerEvents: 'none',
    top: '50%',
    transform: 'translate(0, -50%)',
    minWidth: '2px',
    height: '100%',
    fontSize: '0.75rem',
    backgroundColor: 'white',
  },
  legendText: {
    fontSize: '0.75rem',
  },
});

const getTooltip = (step, idx, arr, continuous, decimals = 2, unitString, classes) => {
  let tooltip = '';
  let unit = '';

  if (unitString !== undefined && unitString !== null) {
    unit = unitString;
  }

  if ((step.min === null || step.min === undefined) && !step.max) {
    return null;
  }

  const min = Number(step.min).toFixed(decimals);
  const max = Number(step.max).toFixed(decimals);

  if (step.max === Number.MAX_VALUE || step.max === undefined) {
    tooltip = `${min.toString().replace('.00', '') + unit} <`;
  } else if (min === max) {
    tooltip = `${min.toString().replace('.00', '') + unit}`;
  } else if (idx === 0) {
    if (continuous) {
      tooltip = `< ${min}`;
    } else {
      tooltip = `${min}${unit}`;
    }

    return (
      <Box
        className={classes.legendText}
        key={idx}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <Box
          style={{ transform: 'translateX(-50%)' }}
          sx={{
            position: 'absolute',
            left: '0%',
          }}
        >
          {tooltip}
        </Box>
        <Box
          style={{ transform: 'translateX(50%)' }}
          sx={{
            position: 'absolute',
            right: '0%',
          }}
        >{`${max}${unit}`}</Box>
      </Box>
    );
  } else if (idx === arr.length - 1) {
    if (!continuous) {
      tooltip = `${max}${unit}`;
    }
  } else {
    tooltip += `${max}${unit}`;
  }

  return (
    <Box
      className={classes.legendText}
      key={idx}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      <Box
        style={{ transform: 'translateX(50%)' }}
        sx={{
          position: 'absolute',
          right: '0%',
        }}
      >
        {tooltip}
      </Box>
    </Box>
  );
};

const ReportLegendColorScale = (props: ReportLegendColorScale.propTypes) => {
  const classes = props.classes;
  const LangFile = useLangFile();

  if (props.scale == null) {
    return null;
  }

  const marginTop = props.showMinMax ? 2.5 : 1.5;

  return (
    <Box
      sx={{
        mt: 1,
        mb: 1,
        width: '100%',
      }}
    >
      <Box
        sx={{
          border: 1,
        }}
      >
        <div className={classes.wrapper}>
          <div className={classes.scale}>
            {[...props.scale].map((step, idx, arr) => {
              return (
                <Box
                  className={classes.scaleStep}
                  key={idx}
                  sx={{
                    bgcolor: step.color,
                    color: step.contrast,
                    flex: 1,
                    height: '16px',
                  }}
                />
              );
            })}

            {props.showMinMax && (
              <Box
                className={classes.minMaxTick}
                sx={{
                  left: `${props.minPercent}%`,
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    height: '100%',
                  }}
                >
                  <Box
                    style={{ transform: `translate(${props.minPercent === 0 ? 0 : -50}%, -2px)` }}
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: '100%',
                      color: 'black',
                    }}
                  >
                    {LangFile.InlineColorScale.low}
                  </Box>
                </Box>
              </Box>
            )}

            {props.showMinMax && (
              <Box
                className={classes.minMaxTick}
                sx={{
                  right: `${100 - props.maxPercent}%`,
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    height: '100%',
                  }}
                >
                  <Box
                    style={{ transform: `translate(${props.maxPercent === 100 ? 0 : 50}%, -2px)` }}
                    sx={{
                      position: 'absolute',
                      right: '50%',
                      top: '100%',
                      color: 'black',
                    }}
                  >
                    {LangFile.InlineColorScale.high}
                  </Box>
                </Box>
              </Box>
            )}
          </div>
        </div>
      </Box>
      <Box
        sx={{
          mt: marginTop,
          paddingLeft: 1.4,
          paddingRight: 1.4,
        }}
      >
        <div className={classes.wrapper}>
          <div className={classes.scale}>
            {[...props.scale].map((step, idx, arr) => {
              return getTooltip(
                step,
                idx,
                arr,
                props.continuous,
                props.decimals,
                props.unit,
                classes
              );
            })}
          </div>
        </div>
      </Box>
    </Box>
  );
};

ReportLegendColorScale.propTypes = {
  scale: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
      contrast: PropTypes.string.isRequired,
    })
  ),
  continuous: PropTypes.bool,
  showMinMax: PropTypes.bool,
  minPercent: PropTypes.number,
  maxPercent: PropTypes.number,
  decimals: PropTypes.number,
  unit: PropTypes.string,
};

ReportLegendColorScale.defaultProps = {
  continuous: false,
  minPercent: 0,
  maxPercent: 100,
  decimals: 2,
};

export default withStyles(styles)(ReportLegendColorScale);
